<script>
import ProofViewFileDetails from "./proof-view-file-details";
import ProofViewComments from "./proof-view-comments";
import ProofViewFreeUserPromo from "./proof-view-free-user-promo";

const DATE_PICKER = 'Datepicker';
export default {
  name: "proof-view-sidebar-right",
  components: {ProofViewComments, ProofViewFileDetails, ProofViewFreeUserPromo},
  props: ["label", "menu", "activeFile", "curAnnotations", "curAnnotationType",
    "activeCommentId", "curUsersTaggable", "commentFileApi",
    "isLiveProofView", "liveProofActiveUrl", "liveProofSizeSelected",
    "usersColor", "isCompareMode", "isMobile", "curUser", "curApprover",
    "avatar", "curAccount", "proofViewVersion", "disabled", "activeEditorAnnotationId", "allowViewAllComments", "currentProof",
    "changeTracking"],
  data() {
    return {
      commentsVisible: true,
      fileDetailsVisible: false,
      thisProofVisible: true,
      DATE_PICKER
    };
  },
  mounted() {
    this.updateWidth();
  },
  updated() {
    this.updateWidth();
  },
  watch: {
  },
  methods: {
    toggle: function() {
      this.$emit('on-toggle');
    },
    updateWidth: function() {
      const sidebarWidth = this.$refs["right-sidebar"].offsetWidth;
      this.$emit('on-width-changed', sidebarWidth);
    },
    showComments: function(){
      this.commentsVisible = true;
    },
    getLabel: function (section, key) {
      return this.$A.LangService.getLabel(section, key);
    },
  },
  computed: {
    numComments: function() {
      return this.allowViewAllComments || this.curUser
      ? _.get(this.curAnnotations, 'length', 0)
      : _.filter(this.curAnnotations, ({ userId, approverId}) => 
      userId || approverId === _.get(this.curApprover, 'id', -1)).length;
    },
    customFields: function () {
      return _.get(this, 'currentProof.customFields', []);
    },
    filteredCustomFields: function () {
      if (!this.customFields || this.customFields.length === 0) {
        return [];
      }

      const grouped = _.groupBy(this.customFields, 'accountProofPropertyId');

      return Object.values(grouped).map(group => _.sortBy(group, 'proofPropertyId')[0]);
    },
    hasCustomFieldValues: function () {
      return this.customFields.some(
        field => field.proofPropertyValue && field.proofPropertyValue.length > 0
      );
    },
    // The sidebar never lists version changes itself — the compare-mode rail
    // owns that. It only surfaces a "compare versions" prompt under File
    // Details when changes were detected for the file being viewed.
    activeFileChangeJob: function () {
      if (!this.activeFile) return null;
      const jobs = _.get(this.changeTracking, 'jobs', []);
      return jobs.find((j) => j.toProofVersionFileId === this.activeFile.id) || null;
    },
    activeFileChangeCount: function () {
      const job = this.activeFileChangeJob;
      return job ? (job.normalChangeCount || 0) : 0;
    },
    showCompareChangesPrompt: function () {
      return !this.isCompareMode && this.activeFileChangeCount > 0;
    },
  },
}
</script>

<template>
  <aside v-cloak
    :class="{'close': !menu, 'open' : menu, 'compare px-2' : isCompareMode || isMobile, 'sidebar' : !isMobile && !isCompareMode}"
    ref="right-sidebar" id="proofViewRightSidebar"
    class="border-start border-gray-100 flex-shrink-0 d-flex px-0 flex-column rounded-0">
    <div v-if="!isCompareMode && !isMobile"
      class="sidebar-header align-items-center border-bottom border-gray-100 px-4 py-3">
      <button class="menu-toggle btn p-0 btn-link d-flex align-items-center gap-1" @click="toggle">
        <img src="/img/review/icons/arrow-right.svg" class="arrow" width="20" alt="">
        <img src="/img/review/icons/menu-line.svg" class="line" width="2" alt="">
      </button>
    </div>
    <div v-cloak v-show="menu" x-transition :id="`${label}-right-bar`" class="sidebar-content flex-fill overflow-auto">
      <div class="d-flex flex-column h-100">
        <div class="accordion" id="accordionSidebar">
          <!-- This Proof -->
          <div class="accordion-item" v-if="currentProof">
            <h2 class="accordion-header position-relative">
              <button class="accordion-button d-flex align-items-center gap-10" type="button"
                :class="{ collapsed: !thisProofVisible }" @click="thisProofVisible = !thisProofVisible">
                <div class="icon">
                  <img src="/img/review/icons/chevron-right.svg" width="20" alt="">
                </div>
                This Proof
              </button>
            </h2>
            <b-collapse v-model="thisProofVisible" id="file-details-collapse" class="accordion-collapse"
              data-parent="#accordionSidebar">
              <div class="accordion-body px-4 py-3 border-top border-gray-100">
                <ul class="list-unstyled d-flex flex-column gap-2 mb-0">
                  <li :class="['p-10', { 'border-bottom border-gray-200': (currentProof && currentProof.dueDate) || hasCustomFieldValues }]" v-if="currentProof && currentProof.name">
                    <h6 class="text-gray-900 fw-semibold mb-1">Proof Name</h6>
                    <p class="text-gray-900 mb-0">{{ currentProof.name }}</p>
                  </li>
                  <li :class="['p-10', { 'border-bottom border-gray-200': hasCustomFieldValues }]"
                    v-if="currentProof && currentProof.dueDate">
                    <h6 class="text-gray-900 fw-semibold mb-1">Due Date</h6>
                    <p class="text-gray-900 mb-0">{{ $A.FormatDateToString(currentProof.dueDate) }}</p>
                  </li>

                  <!-- Custom Fields -->
                  <li v-for="(field, index) in filteredCustomFields"
                    v-if="field.proofPropertyValue && field.proofPropertyValue.length"
                    :class="['p-10', { 'border-bottom border-gray-200': index < filteredCustomFields.length - 1 }]">
                    <h6 class="text-gray-900 fw-semibold mb-1">{{ field.accountProofPropertyLabel }}</h6>
                    <p class="text-gray-900 mb-0" v-for="value in field.proofPropertyValue">
                      {{ field.accountProofPropertyDataType === DATE_PICKER ? $A.FormatDateToString(value) : value }}
                    </p>
                  </li>
                </ul>
              </div>
            </b-collapse>
          </div>
          <!-- Files Details -->
          <div class="accordion-item">
            <h2 class="accordion-header position-relative">
              <button class="accordion-button d-flex align-items-center gap-10" type="button"
                      :class="{collapsed : !fileDetailsVisible}"
                      @click="fileDetailsVisible = !fileDetailsVisible">
                <div class="icon">
                  <img src="/img/review/icons/chevron-right.svg" width="20" alt="">
                </div>
                {{ getLabel('review_screen_labels', 'file_details_header') }}
              </button>
            </h2>
            <b-collapse v-model="fileDetailsVisible" id="file-details-collapse" class="accordion-collapse" data-parent="#accordionSidebar">
              <div class="accordion-body px-4 py-3 border-top border-gray-100">
                <proof-view-file-details :file="activeFile"/>
              </div>
            </b-collapse>
            <!-- always visible, accordion open or closed: detected-changes callout -->
            <div v-if="showCompareChangesPrompt" class="px-4 pb-3" :class="{ 'pt-3 border-top border-gray-100': !fileDetailsVisible }">
              <button type="button"
                      class="changes-prompt"
                      @click="$emit('compare-file-changes', activeFile)">
                <span class="changes-prompt-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" stroke="currentColor" stroke-width="1.5"/>
                    <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" stroke="currentColor" stroke-width="1.5" fill="#fff"/>
                  </svg>
                </span>
                <span class="changes-prompt-body">
                  <span class="changes-prompt-title">New changes detected</span>
                  <span class="changes-prompt-action">Compare versions side by side</span>
                </span>
                <span class="changes-prompt-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </div>
          <!--Comments Details -->
          <div class="accordion-item border-bottom-0">
            <h2 class="accordion-header position-relative">
              <button class="accordion-button d-flex align-items-center gap-10" type="button"
                      :class="{collapsed : !commentsVisible}"
                      @click="commentsVisible = !commentsVisible">
                <div class="icon">
                  <img src="/img/review/icons/chevron-right.svg" width="20" alt="">
                </div>
                {{ getLabel('review_screen_labels', 'comment_toggle_header') }}
              </button>
              <div class="badge badge-gray position-absolute top-50 translate-middle-y end-0 mr-4">{{numComments}}</div>
            </h2>
            <b-collapse v-model="commentsVisible" id="comments-collapse" class="accordion-collapse" data-parent="#accordionSidebar">
              <div class="accordion-body p-0 ">
                <proof-view-comments
                    ref="comments"
                    :label="label"
                    :comments="curAnnotations"
                    :cur-file="activeFile"
                    :cur-user="curUser"
                    :cur-approver="curApprover"
                    :cur-annotation-type="curAnnotationType"
                    :active-comment-id="activeCommentId"
                    :cur-users-taggable="curUsersTaggable"
                    :comment-file-api="commentFileApi"
                    :is-live-proof-view="isLiveProofView"
                    :live-proof-active-url="liveProofActiveUrl"
                    :live-proof-size-selected="liveProofSizeSelected"
                    :users-color="usersColor"
                    :disabled="disabled"
                    :active-editor-annotation-id="activeEditorAnnotationId"
                    :allow-view-all-comments="allowViewAllComments"
                    v-on="$listeners"
                />
              </div>
            </b-collapse>
          </div>
        </div>
        <div class="flex-grow-1"></div>
        <!-- Free User Promo -->
        <proof-view-free-user-promo v-if="!isCompareMode && menu && curAccount.isFreeSku" :avatar="avatar" :cur-account="curAccount" :proof-view-version="proofViewVersion"/>
      </div>
    </div>
  </aside>
</template>

<style scoped>
/* Compare */
.compare {
  width: 100%;
  height: auto !important;
  background: #ffffff;
  border-radius: 10px;
  padding: 10px;
}
.badge{
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 400;
  z-index: 100;
  line-height: 16px;
}
.badge-gray{
  background: #EBEBED;
  color: #252526;
}
.badge-changes{
  background: #6d5bd0;
  color: #ffffff;
}
.badge-changes-done{
  background: #d1f0dd;
  color: #1d7a44;
}
/* "Changes detected" prompt: one clickable card — fact on top, action below */
.changes-prompt{
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  background: rgba(109, 91, 208, 0.06);
  border: 1px solid rgba(109, 91, 208, 0.25);
  border-radius: 8px;
  color: #6d5bd0;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.changes-prompt:hover{
  background: rgba(109, 91, 208, 0.11);
  border-color: rgba(109, 91, 208, 0.45);
}
.changes-prompt:focus-visible{
  outline: 2px solid #6d5bd0;
  outline-offset: 2px;
}
.changes-prompt-icon{
  flex-shrink: 0;
  display: inline-flex;
}
.changes-prompt-body{
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.changes-prompt-title{
  font-size: 13px;
  font-weight: 600;
  color: #252526;
  line-height: 1.3;
}
.changes-prompt-action{
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}
.changes-prompt-arrow{
  flex-shrink: 0;
  font-size: 14px;
  transition: transform 120ms ease;
}
.changes-prompt:hover .changes-prompt-arrow{
  transform: translateX(2px);
}
@media (prefers-reduced-motion: reduce){
  .changes-prompt, .changes-prompt-arrow{ transition: none; }
  .changes-prompt:hover .changes-prompt-arrow{ transform: none; }
}
.accordion-button:not(.collapsed) .icon{
  transform: rotate(90deg);
}
.accordion-button::after{
  display: none;
}
.accordion-item{
  border-bottom: 1px solid #EBEBED;
}
.accordion-item .accordion-button{
  position: relative;
  width: 100%;
  padding: 24px;
  font-size: 1rem;
  color: #575759;
  background-color: #F9F9F9;
  border: 0;
}
.accordion-header{
  margin-bottom: 0px;
}
.sidebar{
  width: 320px;
  background-color: #F9F9F9;
  transition: all ease-in-out 500ms;
  opacity: 1;
}
.sidebar.close .menu-toggle .arrow,
.sidebar.close .menu-toggle .line{
  transition: all ease-in-out 300ms;
}
.sidebar.close .menu-toggle .arrow{
  transform: scale(-1);
}
.sidebar.close .menu-toggle .line{
  order: -1;
}
@media(min-width:1200px){
  .sidebar.close{
    margin-right: -248px;
  }
}
@media(max-width:1200px) and (min-width:992px){
  .sidebar-header{
    display: flex !important;
  }
  .sidebar{
    position: fixed;
    right: 0;
    top: 60px;
    z-index: 10000;
    height: calc(100% - 60px);
  }
  .sidebar.close{
    right: -248px;
  }
}
@media(max-width:992px){
  .sidebar {
    position: static;
    width: 100%;
    height: auto !important;
  }
}
</style>
